import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import { map, Observable, of } from 'rxjs';
import { MenuItem } from 'src/app/components/sidebar/menu-item';

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  protected endpoint = 'menu/sidebar';

  private menuOptions: MenuItem[] = [];
  // Cached full menu; fetched once per page load, survives mode switches.
  private flatMenuAll: MenuItem[] | null = null;
  public showProdMenu = false;
  private activeTab: number = 0;

  constructor(private apiService: ApiService) {}

  // Fetch full menu once, then serve each mode from cache (no refetch on switch).
  getSidebar(mode: 'customer' | 'provider' = 'customer'): Observable<MenuItem[]> {
    if (this.flatMenuAll) {
      const tree = this.filterAndBuild(this.flatMenuAll, mode);
      this.menuOptions = tree;
      return of(tree);
    }

    // No audience param — backend returns all active menus; filtered client-side.
    return this.apiService.get(this.endpoint!, {}, true).pipe(
      map((res: any) => {
        const flatAll = this.transformFlat(
          Array.isArray(res?.data) ? res.data : [],
        );
        this.flatMenuAll = flatAll;

        const tree = this.filterAndBuild(flatAll, mode);
        this.menuOptions = tree;
        return tree;
      }),
    );
  }

  // Current tree for instant paint; empty until the first fetch resolves.
  getCachedMenu(): MenuItem[] {
    return this.menuOptions;
  }

  // Route transforms on the raw flat list (both audiences kept).
  private transformFlat(flatMenuData: MenuItem[]): MenuItem[] {
    const backfilled = this.backfillParents(flatMenuData);
    return backfilled.map((item: any) => {
      if (item.route === 'accounts' || item.route === '/accounts') {
        return {
          ...item,
          name: 'Instant Rate Manager',
          route: 'instant-rate-manager',
        };
      }
      if (item.route && item.route.includes('accounts/instant-rates-manager')) {
        return {
          ...item,
          route: item.route
            .replace('accounts/instant-rates-manager', 'instant-rate-manager')
            .replace(/\/$/, ''),
        };
      }
      return item;
    });
  }

  // Recovers parent/grandparent rows referenced only via a child's nested `parent`.
  private backfillParents(flatMenuData: MenuItem[]): MenuItem[] {
    const byId = new Map<string, MenuItem>(
      flatMenuData.map((item) => [String(item.id), item]),
    );
    flatMenuData.forEach((item: any) => {
      let parent = item.parent as (MenuItem & { parent?: MenuItem }) | undefined;
      while (parent && !byId.has(String(parent.id))) {
        byId.set(String(parent.id), parent);
        parent = (parent as any).parent;
      }
    });
    return Array.from(byId.values());
  }

  // Filters the flat list by mode + environment, then builds the hierarchy.
  private filterAndBuild(
    flatAll: MenuItem[],
    mode: 'customer' | 'provider',
  ): MenuItem[] {
    const filtered = flatAll.filter((menu) => {
      const audienceOk =
        !menu.audience || menu.audience === 'all' || menu.audience === mode;
      const envOk = this.showProdMenu ? menu.environment === 'Prod' : true;
      return audienceOk && envOk;
    });
    return this.buildHierarchy(this.pruneOrphans(filtered));
  }

  // Drops children whose parent was filtered out, so they aren't promoted to root.
  private pruneOrphans(filtered: MenuItem[]): MenuItem[] {
    const isRoot = (item: MenuItem) =>
      item.parent_id === null ||
      String(item.parent_id) === '0';

    let survivors = filtered;
    let changed = true;
    while (changed) {
      const ids = new Set(survivors.map((item) => String(item.id)));
      const next = survivors.filter(
        (item) => isRoot(item) || ids.has(String(item.parent_id)),
      );
      changed = next.length !== survivors.length;
      survivors = next;
    }
    return survivors;
  }

  /**
   * Determines the active tab index based on current route
   */
  setActiveTab(): void {
    const currentPage = window.location.pathname;

    const findTabIndex = (menu: MenuItem, index: number): number | null => {
      if (menu.route === currentPage || menu.route === currentPage.slice(1)) {
        return index;
      }

      for (const child of menu.children || []) {
        const childIndex = findTabIndex(child, index);
        if (childIndex !== null) {
          return childIndex;
        }
      }

      return null;
    };

    this.activeTab = this.menuOptions.findIndex((menu, index) => {
      return findTabIndex(menu, index) !== null;
    });
  }

  /**
   * Returns the active tab index
   */
  getActiveTab(): number {
    return this.activeTab;
  }

  /**
   * Sorts menu items by their `order` value
   */
  private sortByMenuOrder(arr: MenuItem[]): MenuItem[] {
    return arr.sort((a, b) => {
      const orderA = parseInt(a.order as any, 10);
      const orderB = parseInt(b.order as any, 10);

      if (isNaN(orderA) && isNaN(orderB)) return 0;
      if (isNaN(orderA)) return 1;
      if (isNaN(orderB)) return -1;

      return orderA - orderB;
    });
  }

  /**
   * Converts a flat list of menu items into a nested hierarchy
   */
  private buildHierarchy(flatData: MenuItem[]): MenuItem[] {
    const menuMap: { [key: string]: MenuItem } = {};
    const rootMenu: MenuItem[] = [];

    flatData.forEach(item => {
      menuMap[item.id] = { ...item, children: [] };
    });

    flatData.forEach(item => {
      const current = menuMap[item.id];
      const parent = menuMap[String(item.parent_id)];

      if (String(item.parent_id) === '0' || !parent) {
        rootMenu.push(current);
      } else {
        parent.children?.push(current);
      }
    });

    Object.values(menuMap).forEach(item => {
      if (item.children?.length) {
        item.children = this.sortByMenuOrder(item.children);
      }
    });

    return this.sortByMenuOrder(rootMenu);
  }
}
